import React from "react";
import { Form, Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faCheckCircle, 
  faTimesCircle,
  faMinusCircle 
} from "@fortawesome/free-solid-svg-icons";

import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import CardDropdown from "components/common/CardDropdown";

export default function HealthChecksTable({
  healthChecks,
  onEdit,
  onToggleActive,
  setShowDeleteModal,
  setHealthCheckToDelete,
  page,
  pageSize,
  totalPages,
  totalElements,
  setPage,
  setPageSize,
}) {
  
  const confirmDelete = (id) => {
    setHealthCheckToDelete(id);
    setShowDeleteModal(true);
  };

  const getSeverityStyle = (value) => {
    const severityStyles = {
      INFO: { bg: "#e3f2fd", text: "#1565c0", border: "#bbdefb" },
      ALERT: { bg: "#fef9c3", text: "#a16207", border: "#fde68a" },
      MEDIUM: { bg: "#ffedd5", text: "#c2410c", border: "#fdba74" },
      CRITICAL: { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" },
      WARNING: { bg: "#fef9c3", text: "#a16207", border: "#fde68a" },
      DEFAULT: { bg: "#f5f5f5", text: "#616161", border: "#e0e0e0" },
    };
    return severityStyles[value] || severityStyles.DEFAULT;
  };

  const columns = [
    {
      accessorKey: "oliveebApp",
      header: "App / Service",
      cell: ({ row }) => {
        const { name, oliveebApp } = row.original;
        return (
          <div className="py-1">
            <h6 className="mb-0 text-900 fs-10">{name}</h6>
            <div className="text-500 font-monospace" style={{ fontSize: '0.65rem' }}>
              {oliveebApp}
            </div>
          </div>
        );
      },
      meta: { headerProps: { className: "text-900" } }
    },
    {
      accessorKey: "active",
      header: "Enabled",
      cell: ({ row }) => {
        const { active, isEnabled, id } = row.original;
        const enabled = typeof active === "boolean" ? active : !!isEnabled;
        return (
          <Form.Check 
            type="switch"
            id={`active-toggle-${id}`}
            checked={enabled}
            onChange={() => onToggleActive(row.original)}
            title={enabled ? "Disable check" : "Enable check"}
            className="cursor-pointer d-flex justify-content-center"
          />
        );
      },
      meta: { 
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" } 
      }
    },
    {
      accessorKey: "severity",
      header: "Status",
      cell: ({ getValue }) => {
        const value = getValue() || "INFO";
        const style = getSeverityStyle(value);

        return (
          <span 
            style={{
              backgroundColor: style.bg,
              color: style.text,
              border: `1px solid ${style.border}`,
              padding: "2px 6px",
              borderRadius: "4px",   
              fontSize: "0.7rem",   
              fontWeight: 600,
              cursor: "default"
            }}
          >
            {value}
          </span>
        );
      },
      meta: { 
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" } 
      }
    },
    {
      accessorKey: "isHealthy",
      header: "Health",
      cell: ({ getValue }) => {
        const isHealthy = getValue();
        const softGlowStyle = {
          filter: isHealthy === true
            ? "drop-shadow(0 0 4px rgba(46, 125, 50, 0.35))"
            : isHealthy === false
            ? "drop-shadow(0 0 4px rgba(198, 40, 40, 0.35))"
            : "drop-shadow(0 0 4px rgba(100, 116, 139, 0.28))",
          fontSize: "1rem"
        };

        return (
          <div className="d-flex align-items-center justify-content-center">
            <FontAwesomeIcon
              icon={isHealthy === true ? faCheckCircle : isHealthy === false ? faTimesCircle : faMinusCircle}
              className={isHealthy === true ? "text-success" : isHealthy === false ? "text-danger" : "text-secondary"}
              style={softGlowStyle}
            />
          </div>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" }
      }
    },
    {
      accessorKey: "lastCheckedAt",
      header: "Last Updated At",
      cell: ({ getValue }) => {
        const date = getValue();
        return (
          <span 
            className="text-500 d-inline-block"
            style={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.4 }}
          >
            {date ? new Date(date).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : "Never"}
          </span>
        );
      },
      meta: { 
        headerProps: { className: "text-900" }
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <CardDropdown drop="start">
          <div className="py-2">
            <Dropdown.Item onClick={() => onEdit(row.original)}>View/edit</Dropdown.Item>
            <Dropdown.Item onClick={() => confirmDelete(row.original.id)} className="text-danger">
              Delete
            </Dropdown.Item>
          </div>
        </CardDropdown>
      ),
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" }
      }
    }
  ];

  const table = useAdvanceTable({
    data: healthChecks || [],
    columns,
    sortable: true,
    pagination: true,
    perPage: pageSize,
  });

  return (
    <AdvanceTableProvider {...table}>
      <div className="mx-n1 px-1">
        <AdvanceTable
          headerClassName="bg-light text-nowrap align-middle text-dark"
          rowClassName="align-middle white-space-nowrap fs-10"
          tableProps={{
            bordered: true,
            striped: true,
            className: "mb-0 overflow-hidden",
          }}
        />
      </div>

      <div className="mt-3">
        <TablePaginationFooter
          page={page}
          totalPages={totalPages || 0}
          totalElements={totalElements || 0}
          onPrev={page > 0 && totalElements > 0 ? () => setPage(page - 1) : undefined}
          onNext={page < (totalPages - 1) && totalElements > 0 ? () => setPage(page + 1) : undefined}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            if (typeof setPageSize === 'function') {
              setPageSize(size);
              setPage(0);
            }
          }}
          pageSizeOptions={[10, 20, 30, 50, 100]}
        />
      </div>
    </AdvanceTableProvider>
  );
}